import React from 'react';
import { connect } from 'react-redux';
import { t } from 'Src/i18n';
import type { updateCartParams } from 'Components/widgets/cart/cart.actions';
import { updateBookingCart } from 'Components/widgets/cart/cart.actions';
import { useAppContext } from 'Context/ReactAppContext';
import { TRACK_EVENT_NAMES } from 'Tracking/constants';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer';
import { TransfersSection } from 'Components/widgets/package/transfers-section/TransfersSection';
import { TransferBulletPoints } from 'Views/booking/transport/TransferBulletPoints';
import { Tag } from 'Components/frameworks/tag/Tag';
import { Title } from 'Components/frameworks/title/Title';
import { TRANSFER_TYPE_ENUM } from 'Views/booking/constants';

const prepareTransferScheduleParameter = ({ pricingInfo: { schedules = [] } = {} } = {}) =>
  schedules.map((schedule) => ({
    attendees: schedule.attendees,
    end_date: schedule.endDate,
    start_date: schedule.startDate,
  }));

type Props = {
  availableTransfers: ReadonlyArray<UpsellTransfer>;
  bookingId: string;
  addedTransferId: string;
  stayIndex: number;
  updateCart: (arg0: updateCartParams) => Promise<unknown>;
};
export const TransfersLayer = ({
  availableTransfers,
  bookingId,
  addedTransferId,
  stayIndex,
  updateCart,
}: Props) => {
  const appContext = useAppContext();
  const { trackClickEvent } = useTrackEvent();
  const selectedTransfers = availableTransfers.filter(
    ({ pricingInfo: { id } }) => id === addedTransferId,
  );
  const { directionType = TRANSFER_TYPE_ENUM.ROUND_TRIP } = availableTransfers?.[0] || {};
  const isOneWayTransfer = directionType !== TRANSFER_TYPE_ENUM.ROUND_TRIP;
  const transferBulletPointTitle = 'common.transport.transfer.title';
  let transferBulletPoints = [
    'common.transport.transfer.arrivalPoint',
    'common.transport.transfer.roundTrip',
  ];

  if (isOneWayTransfer) {
    const isOutBoundTransfer = directionType === TRANSFER_TYPE_ENUM.OUTBOUND;
    const isInBoundTransfer = directionType === TRANSFER_TYPE_ENUM.INBOUND;

    if (isOutBoundTransfer) {
      transferBulletPoints = ['common.transport.transfer.outboundOnlyIncluded'];
    }

    if (isInBoundTransfer) {
      transferBulletPoints = [
        'common.transport.transfer.arrivalPoint',
        'common.transport.transfer.inboundOnlyIncluded',
      ];
    }
  }

  const removeTransfer = () => {
    trackClickEvent(TRACK_EVENT_NAMES.CLICK_UPSELL, {
      detail_click: 'remove_transfer',
    });
    return updateCart({
      action: 'remove',
      bookingId,
      context: appContext,
      id: addedTransferId,
      serviceName: 'TRANSFER',
      stayIndex,
    });
  };

  const addTransfer = (transfer) => {
    trackClickEvent(TRACK_EVENT_NAMES.CLICK_UPSELL, {
      detail_click: 'add_transfer',
    });
    return updateCart({
      action: 'update',
      bookingId,
      context: appContext,
      id: transfer.pricingInfo.id,
      schedules: prepareTransferScheduleParameter(transfer),
      serviceName: 'TRANSFER',
      stayIndex,
    });
  };

  return (
    <SiteContainer className="py-20">
      <Tag className="mb-10 border-sienna bg-sienna text-white">
        {t('booking.confirmation.onDemand')}
      </Tag>
      <Title className="mb-20" theme="title-brand-n0">
        {t('common.transport.transfer.transferOnDemand')}
      </Title>
      <TransfersSection
        addTransfer={addTransfer}
        availableTransfers={availableTransfers}
        bookingId={bookingId}
        removeTransfer={removeTransfer}
        selectedTransfers={selectedTransfers}
      />
      <div className="mt-20">
        <TransferBulletPoints
          title={transferBulletPointTitle}
          bulletPoints={transferBulletPoints}
        />
      </div>
    </SiteContainer>
  );
};

const mapStateToProps = (state, { bookingId, stayIndex }) => {
  const cartBookings = state.memberSpace.cart?.data?.bookingsCart?.cartBookings || [];
  const cartBooking = cartBookings.find((booking) => booking.bookingId === bookingId);
  return {
    addedTransferId: (cartBooking?.stayServices || []).find(
      (stayService) => stayService.stayIndex === stayIndex,
    )?.transfers?.[0]?.id,
  };
};

const mapDispatchToProps = {
  updateCart: updateBookingCart,
};
export const TransfersLayerConnected = connect(mapStateToProps, mapDispatchToProps)(TransfersLayer);
